import React, { useEffect, useState } from "react";
import { UserCog, Plus, Copy, Ban, ExternalLink, Palette } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function DesignerAccessTab() {
  const [links, setLinks] = useState(null);
  const [profiles, setProfiles] = useState(null);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    try {
      const [l, p] = await Promise.all([api.get("/branding/designer-links"), api.get("/branding/profiles")]);
      setLinks(l.data); setProfiles(p.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => { load(); }, []);

  const createLink = async () => {
    if (!label.trim()) return toast.error("Bitte eine Bezeichnung angeben");
    setCreating(true);
    try {
      await api.post("/branding/designer-links", { label: label.trim() });
      setLabel("");
      await load();
      toast.success("Designer-Zugangslink erstellt");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setCreating(false); }
  };

  const revokeLink = async (linkId) => {
    if (!window.confirm("Diesen Zugangslink wirklich widerrufen? Der Designer wird sofort ausgeloggt.")) return;
    try {
      await api.delete(`/branding/designer-links/${linkId}`);
      await load();
      toast.success("Link widerrufen");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const copyLink = async (linkId, token) => {
    const url = `${window.location.origin}/designer-access/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(linkId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="designer-access-tab" className="space-y-8">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
          <UserCog size={11} strokeWidth={1.5} /> Branding Studio · Designer-Zugang
        </div>
        <div className="font-heading text-xl font-light tracking-tighter text-slate-900">
          Zugangslinks für Designer:innen
        </div>
        <p className="text-xs text-slate-500 mt-1 max-w-2xl">
          Jeder Link öffnet ein eigenständiges, stark eingeschränktes Studio — Designer:innen können dort
          NUR Branding-Profile (Logo, Favicon, Hintergrund, Farben, Firmenname) anlegen und bearbeiten.
          Kein Zugriff auf Kunden, Termine, Rechnungen oder andere Funktionen.
        </p>
      </div>

      <div className="border border-slate-200 bg-white p-5" data-testid="designer-link-create-card">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Bezeichnung</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createLink()}
              placeholder="z.B. Design-Team Müller" className="input-tech" data-testid="designer-link-label-input" />
          </div>
          <button onClick={createLink} disabled={creating} data-testid="designer-link-create-btn"
            className="px-4 py-2.5 bg-slate-900 text-white text-xs font-medium hover:bg-black disabled:opacity-50 flex items-center gap-1.5">
            <Plus size={12} strokeWidth={1.5} /> {creating ? "Erstellt…" : "Zugangslink erstellen"}
          </button>
        </div>
      </div>

      <div className="border border-slate-200 bg-white" data-testid="designer-links-table">
        <div className="p-4 border-b border-slate-200 text-xs text-slate-500">Zugangslinks</div>
        {!links ? (
          <div className="text-sm text-slate-400 italic py-10 text-center">Lädt…</div>
        ) : links.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-10 text-center">Noch keine Zugangslinks erstellt.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {links.map((l) => (
              <li key={l.link_id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`designer-link-row-${l.link_id}`}>
                <span className="text-sm font-medium text-slate-900 w-48 truncate">{l.label}</span>
                {l.revoked ? (
                  <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 border border-slate-200">Widerrufen</span>
                ) : (
                  <>
                    <button onClick={() => copyLink(l.link_id, l.token)} data-testid={`designer-link-copy-${l.link_id}`}
                      className="text-[11px] text-[#00807E] hover:underline flex items-center gap-1">
                      <Copy size={10} strokeWidth={1.5} /> {copiedId === l.link_id ? "✓ Kopiert" : "Link kopieren"}
                    </button>
                    <button onClick={() => revokeLink(l.link_id)} data-testid={`designer-link-revoke-${l.link_id}`}
                      className="text-[11px] text-red-600 hover:underline flex items-center gap-1">
                      <Ban size={10} strokeWidth={1.5} /> Widerrufen
                    </button>
                  </>
                )}
                <span className="ml-auto text-[10px] text-slate-400 font-mono">
                  {l.last_used_at ? `Zuletzt genutzt: ${l.last_used_at.slice(0, 10)}` : "Noch nicht genutzt"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border border-slate-200 bg-white" data-testid="designer-profiles-overview">
        <div className="p-4 border-b border-slate-200 text-xs text-slate-500 flex items-center gap-2">
          <Palette size={11} strokeWidth={1.5} /> Branding-Profile (von Designer:innen gepflegt)
        </div>
        {!profiles ? (
          <div className="text-sm text-slate-400 italic py-10 text-center">Lädt…</div>
        ) : profiles.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-10 text-center">Noch keine Branding-Profile angelegt.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {profiles.map((p) => (
              <li key={p.profile_id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`designer-profile-row-${p.profile_id}`}>
                <span className="w-4 h-4 rounded-full border border-slate-200 shrink-0" style={{ background: p.primary_color }} />
                <span className="text-sm font-medium text-slate-900">{p.name}</span>
                <span className="text-[11px] text-slate-500">{p.company_name}</span>
                <a href={`/brand-preview/${p.profile_id}`} target="_blank" rel="noopener noreferrer"
                  data-testid={`designer-profile-preview-${p.profile_id}`}
                  className="ml-auto text-[11px] text-[#00807E] hover:underline flex items-center gap-1">
                  Vorschau <ExternalLink size={10} strokeWidth={1.5} />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
